// ============================================================================
// piecesJointes — tri des fichiers joints a une publication
// ============================================================================
// Pieces jointes MULTIPLES d'une publication : images, PDF, PowerPoint
// (.pptx seulement — jamais un fichier a macros .pptm ni l'ancien .ppt). Tri
// pur, sans React : quel genre de fichier, accepte ou refuse et pourquoi, et la
// taille lisible. C'est une AFFORDANCE qui evite de perdre un brouillon : la
// verite est SERVEUR (P-UP-2/7 — octets magiques, archive Office verifiee,
// macros refusees, plafonds ; server/blobs/upload.ts, surface 'publications').
// Les bornes sont les memes : 5 Mo pour une image, 10 Mo pour un document,
// dix pieces par publication.

export type AttachmentKind = 'image' | 'pdf' | 'pptx';

export interface Attachment {
  id: string;
  kind: AttachmentKind;
  name: string;
  size: number;
  file: File;
  /** Object URL local (blob:) — images seulement, pour la vignette. */
  url?: string;
}

/**
 * Ce qu'il faut pour MONTRER une piece jointe : celle de l'editeur (fichier
 * local) comme celle du fil (servie par le proxy, url resolue ou pas encore).
 */
export type AttachmentView = Pick<Attachment, 'id' | 'kind' | 'name' | 'size' | 'url'>;

export type RefusalReason = 'type' | 'size' | 'count' | 'duplicate';

export interface Refusal {
  name: string;
  reason: RefusalReason;
  /** Borne de taille depassee, en Mo (reason 'size'). */
  maxMb?: number;
}

export const ATTACHMENT_MAX_FILES = 10;
const MB = 1024 * 1024;
export const ATTACHMENT_MAX_BYTES: Record<AttachmentKind, number> = {
  image: 5 * MB,
  pdf: 10 * MB,
  pptx: 10 * MB,
};

const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const PDF_TYPE = 'application/pdf';
const PPTX_TYPE = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';

/** Filtre du selecteur de fichiers — une affordance, le tri ci-dessous tranche. */
export const ATTACHMENT_ACCEPT = [...IMAGE_TYPES, PDF_TYPE, '.pptx', PPTX_TYPE].join(',');

function extension(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot < 0 ? '' : name.slice(dot + 1).toLowerCase();
}

/**
 * Le genre d'un fichier, ou null s'il est refuse. Certains navigateurs ne
 * donnent aucun type pour un .pptx ou un .pdf : l'extension tranche alors,
 * jamais quand le type annonce autre chose (un .pptx a macros se declare
 * application/vnd.ms-powerpoint.presentation.macroEnabled.12).
 */
export function attachmentKind(file: { name: string; type: string }): AttachmentKind | null {
  const ext = extension(file.name);
  if (IMAGE_TYPES.includes(file.type)) return 'image';
  if (file.type === PDF_TYPE || (file.type === '' && ext === 'pdf')) return 'pdf';
  if (ext === 'pptx' && (file.type === PPTX_TYPE || file.type === '')) return 'pptx';
  return null;
}

/**
 * Trie un lot de fichiers contre ceux deja joints : les acceptes dans l'ordre
 * recu, et pour chaque refus sa raison. Un meme fichier (nom et taille) ne se
 * joint pas deux fois ; au-dela de ATTACHMENT_MAX_FILES, le surplus est refuse.
 */
export function sortFiles(
  existing: ReadonlyArray<Pick<Attachment, 'name' | 'size'>>,
  files: ReadonlyArray<File>,
): { accepted: Array<{ file: File; kind: AttachmentKind }>; refused: Refusal[] } {
  const accepted: Array<{ file: File; kind: AttachmentKind }> = [];
  const refused: Refusal[] = [];
  const seen = new Set(existing.map((a) => `${a.name}|${a.size}`));
  for (const file of files) {
    const kind = attachmentKind(file);
    if (!kind) {
      refused.push({ name: file.name, reason: 'type' });
      continue;
    }
    if (file.size > ATTACHMENT_MAX_BYTES[kind]) {
      refused.push({ name: file.name, reason: 'size', maxMb: ATTACHMENT_MAX_BYTES[kind] / MB });
      continue;
    }
    const key = `${file.name}|${file.size}`;
    if (seen.has(key)) {
      refused.push({ name: file.name, reason: 'duplicate' });
      continue;
    }
    if (existing.length + accepted.length >= ATTACHMENT_MAX_FILES) {
      refused.push({ name: file.name, reason: 'count' });
      continue;
    }
    seen.add(key);
    accepted.push({ file, kind });
  }
  return { accepted, refused };
}

/**
 * Taille lisible : en Ko (entier, 1 au moins) sous 1 Mo, en Mo a une decimale
 * au-dela. L'unite est rendue par l'appelant (cles i18n), jamais ecrite ici.
 */
export function formatSize(bytes: number, locale: string): { amount: string; unit: 'kb' | 'mb' } {
  const nf = (digits: number) =>
    new Intl.NumberFormat(locale.startsWith('en') ? 'en-CA' : 'fr-CA', {
      maximumFractionDigits: digits,
      minimumFractionDigits: digits,
    });
  if (bytes < MB) return { amount: nf(0).format(Math.max(1, Math.round(bytes / 1024))), unit: 'kb' };
  return { amount: nf(1).format(bytes / MB), unit: 'mb' };
}
